// ── Auth / RBAC ──

export interface AuthUser {
  id: number;
  username: string;
  role: string;
  role_label: string;
  org_id: number | null;
  org_name: string | null;
  org_logo: string | null;
  org_banner: string | null;
  display_name: string | null;
  email: string | null;
  avatar: string | null;
  permissions: string[];
}

export interface UserProfile {
  id: number;
  username: string;
  role: string;
  role_label: string;
  display_name: string | null;
  email: string | null;
  avatar: string | null;
}

export interface RoleInfo {
  name: string;
  label: string;
  permissions: string[];
}

export interface OrgCustomRole {
  id: number;
  org_id: number;
  name: string;
  label: string;
  permissions: string[];
}

export interface Permission {
  id: number;
  value: string;
  label: string;
  resource: string;
  action: string;
  access_level: string;
  is_system: number;
  enabled: number;
}

export interface Organization {
  id: number;
  name: string;
  slug: string;
  logo: string | null;
  banner: string | null;
  user_count?: number;
  candidate_count?: number;
  archived_count?: number;
  position_count?: number;
  matrix_count?: number;
  created_at: string;
}

export interface User {
  id: number;
  username: string;
  role: string;
  role_label: string;
  org_id: number | null;
  org_name: string | null;
  created_by: number | null;
  created_at: string;
}

export function hasPermission(user: AuthUser, permission: string): boolean {
  return user.permissions.includes(permission);
}

export function canSeeTab(user: AuthUser, tab: string): boolean {
  return user.permissions.includes(`tab:${tab}`);
}

export function isSuperAdmin(user: { role: string } | null | undefined): boolean {
  return user?.role === "super_admin";
}

// ── JWT helpers (client-side decode, no signature verification) ──

function base64urlDecode(str: string): string {
  let padded = str.replace(/-/g, "+").replace(/_/g, "/");
  while (padded.length % 4) padded += "=";
  return atob(padded);
}

export interface JwtPayload {
  sub: number;
  username: string;
  role: string;
  org_id: number | null;
  permissions: string[];
  iat: number;
  exp: number;
}

export function parseJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    return JSON.parse(base64urlDecode(parts[1])) as JwtPayload;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = parseJwt(token);
  if (!payload) return true;
  return payload.exp < Math.floor(Date.now() / 1000);
}

// ── Token storage ──

const TOKEN_KEY = "rs_jwt";

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function buildAuthHeader(): Record<string, string> {
  const token = getStoredToken();
  return token ? { authorization: `Bearer ${token}` } : {};
}

// ── Domain types ──

export interface Criterion {
  name: string;
  weight: number;
  requirement: string;
}

export const SENIORITY_OPTIONS = [
  "Junior",
  "Mid-level",
  "Senior",
  "Lead",
  "Principal",
  "Executive",
] as const;
export type Seniority = (typeof SENIORITY_OPTIONS)[number];

export const INDUSTRY_OPTIONS = [
  "IT & Software",
  "Finance",
  "Healthcare",
  "Education",
  "Marketing",
  "Engineering",
  "Design",
  "Operations",
  "Sales",
  "Legal",
  "Human Resources",
  "Other",
] as const;
export type Industry = (typeof INDUSTRY_OPTIONS)[number];

export const EMPLOYMENT_TYPE_OPTIONS = [
  "Full-time",
  "Part-time",
  "Contract",
  "Internship",
] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPE_OPTIONS)[number];

export const LOCATION_OPTIONS = [
  "Remote",
  "On-site",
  "Hybrid",
] as const;
export type LocationType = (typeof LOCATION_OPTIONS)[number];

export interface Position {
  id: number;
  title: string;
  description: string | null;
  seniority: Seniority | null;
  industry: Industry | null;
  employment_type: EmploymentType | null;
  location: LocationType | null;
  org_id: number | null;
  org_name: string | null;
  created_at: string;
}

export const WORK_ARRANGEMENT_OPTIONS = ["WFH", "On-Site", "Hybrid"] as const;
export type WorkArrangement = typeof WORK_ARRANGEMENT_OPTIONS[number];

export type MatrixFilter =
  | { id: string; type: "age"; label: string; min?: number; max?: number }
  | { id: string; type: "gender" | "nationality" | "location" | "work_arrangement"; label: string; operator: "in" | "not_in"; values: string[] }
  | {
      id: string;
      type: "custom";
      label: string;
      field_description: string;
      operator: "contains" | "not_contains" | "equals" | "not_equals";
      values: string[];
    };

export interface FilterResult {
  filter_id: string;
  label: string;
  status: "pass" | "fail" | "indeterminate";
  detected_value: string | number | null;
  reason: string;
}

export type MatrixEmploymentType = "regular" | "contractual" | "intern";

export interface HiringMatrix {
  id: number;
  position_id: number;
  position_title?: string;
  name: string;
  employment_type: MatrixEmploymentType;
  criteria: Criterion[];
  filters?: MatrixFilter[];
  created_at: string;
}

export interface MatrixBreakdown {
  criterion: string;
  weight: number;
  score: number;
  notes: string;
}

export interface CandidateNote {
  id: number;
  candidate_id: number;
  content: string;
  created_by: number | null;
  author_name: string | null;
  author_display_name: string | null;
  created_at: string;
  updated_at: string | null;
}

export const INTERVIEW_TYPES = ["phone", "video", "onsite", "technical", "behavioral", "final"] as const;
export type InterviewType = typeof INTERVIEW_TYPES[number];

export const INTERVIEW_STATUSES = ["scheduled", "completed", "cancelled", "no_show"] as const;
export type InterviewStatus = typeof INTERVIEW_STATUSES[number];

export const INTERVIEW_OUTCOMES = ["pass", "fail", "pending"] as const;
export type InterviewOutcome = typeof INTERVIEW_OUTCOMES[number];

export interface Interview {
  id: number;
  candidate_id: number;
  candidate_name?: string | null;
  candidate_filename?: string | null;
  scheduled_at: string;
  duration_minutes: number;
  interview_type: InterviewType;
  status: InterviewStatus;
  outcome: InterviewOutcome | null;
  location: string | null;
  interviewer_id: number | null;
  interviewer_name: string | null;
  interviewer_username?: string | null;
  interviewer_display_name?: string | null;
  notes: string | null;
  created_at: string;
}

// ── Endorsement types ──

export type RatingLevel = "exceeds" | "meets" | "below";

export interface CriterionRow {
  label: string;
  rating: RatingLevel;
  remarks: string;
}

export interface EducationItem {
  school: string;
  degree: string;
  year: string;
}

export interface WorkItem {
  company: string;
  title: string;
  from: string;
  to: string;
  description: string;
}

export interface PersonalData {
  birth_date?: string;
  birth_place?: string;
  age?: string | number;
  height?: string;
  weight?: string;
  nationality?: string;
  civil_status?: string;
  religion?: string;
  mother_name?: string;
  father_name?: string;
}

export interface EndorsementInput {
  organizationName: string;
  orgBanner?: string | null;
  orgLogo?: string | null;
  date: string;
  candidateName: string;
  positionTitle: string;
  introParagraph: string;
  currentSalary: string;
  expectedSalary: string;
  availability: string;
  educationRow: CriterionRow;
  experienceRow: CriterionRow;
  skillsRow: CriterionRow;
  overallRow: CriterionRow;
  recruiterRemarks?: string;
  photoDataUrl?: string | null;
  careerObjective?: string;
  personal?: PersonalData;
  education?: EducationItem[];
  work?: WorkItem[];
  training?: string[];
  resumeText?: string;
  resumeFilename?: string;
}

export function ratingToColumn(rating: RatingLevel): 0 | 1 | 2 {
  return rating === "exceeds" ? 0 : rating === "meets" ? 1 : 2;
}

export function scoreToRating(score: number): RatingLevel {
  if (score >= 80) return "exceeds";
  if (score >= 60) return "meets";
  return "below";
}

export interface Candidate {
  id: number;
  position_id: number;
  matrix_id: number;
  org_id: number | null;
  org_name: string | null;
  position_title?: string | null;
  matrix_name?: string | null;
  filename: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  years_experience: number | null;
  education: string | null;
  skills: string[];
  match_score: number;
  matrix_breakdown: MatrixBreakdown[] | null;
  recommendation: "strong_hire" | "consider" | "reject";
  summary: string | null;
  photo: string | null;
  archived: boolean;
  current_stage: string | null;
  filter_results?: FilterResult[];
  auto_rejected?: boolean;
  resume_pdf_key?: string | null;
  resume_pdf_mime?: string | null;
  created_at: string;
}
